import { Inventory } from "Classes/InventoryService";
import { Intersection, Matrix3, Object3D, Quaternion, Vector2, Vector3 } from "three";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler";

import { AssetType, IRobotJoints } from "./enums";
import { selectionEvents } from "./Events";
import { builder } from "./global";
import { GraphicService } from "./GraphicService";
import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";

let dragging = false;
let mouseDown = false;
let firstMove = true;
const pointerStart = new Vector2();
const payload: { obj: Robot | StaticMesh | undefined; addedToScene: boolean; id: string } = {
    obj: undefined,
    addedToScene: false,
    id: "",
};

let bsuSelectedArm: Robot | undefined = undefined;
let selectedJoints: IRobotJoints | undefined = undefined;
function onArmHover(event: PointerEvent): void {
    if (dragging) return;
    const mouse = new Vector2();
    mouse.x = (event.clientX / GraphicService.divRef.clientWidth) * 2 - 1;
    mouse.y = -(event.clientY / GraphicService.divRef.clientHeight) * 2 + 1;
    const intersect = checkIntersect(mouse, GraphicService.armMeshes);

    if (intersect) {
        const bsu = intersect.object.userData.bsu as Robot;
        if (bsu.isSleepMode) return;
        bsuSelectedArm = bsu;
        const shoulder = bsu.getJointPos(2);
        const elbow = bsu.getJointPos(4);
        const wrist = bsu.getJointPos(6);
        const effector = bsu.getEffectorPos();
        selectedJoints = intersect.object.userData.joint;

        switch (selectedJoints) {
            case IRobotJoints.Elbow:
                GraphicService.sphereHelper.findRadiusAndCenter(elbow, shoulder, wrist);
                break;
            case IRobotJoints.Wrist:
                const radius = new Vector3().subVectors(effector, wrist).length();
                if (radius == undefined || Number.isNaN(radius)) return;
                GraphicService.sphereHelper.updateRadius(radius);
                GraphicService.sphereHelper.position.copy(effector);
                break;
        }
    } else {
        bsuSelectedArm = undefined;
        GraphicService.sphereHelper.updateRadius(0);
    }
}
function onMouseMove(event: PointerEvent): void {
    if (!GraphicService.divRef) return;
    handlePointerMove(event.clientX, event.clientY);
}
function getIntersectPosition(mouse: Vector2, target: Object3D[]): undefined | Intersection {
    const intersect = checkIntersect(mouse, target);
    if (!intersect) return undefined;
    return intersect;
}
function onMouseDown(event: PointerEvent): void {
    if (!GraphicService.divRef) return;
    handlePointerDown(event.clientX, event.clientY);
}

function onMouseUp(event: PointerEvent): void {
    //if (!GraphicService.divRef) return;
    handlePointerUp(event.clientX, event.clientY);
}
function handlePointerMove(x: number, y: number): void {
    const mouse = getPointer(x, y);
    if (pointerStart.distanceTo(mouse) < 0.01) return;
    if (payload.obj) {
        processPayload(payload);
        return;
    }
    if (!mouseDown) return;
    if (bsuSelectedArm) {
        const intersect = checkIntersect(mouse, [GraphicService.sphereHelper]);
        if (intersect) {
            dragging = true;
            if (firstMove) {
                selectionEvents.emit("startDragging");
                firstMove = false;
            }
            switch (selectedJoints) {
                case IRobotJoints.Elbow:
                    // if (!bsu.targetPos && !bsu.targetOr) {
                    //     bsu.targetPos = builder.room.getPortTransform(
                    //         bsu.lightColorName
                    //     )?.target;
                    //     bsu.targetOr = builder.room.getPortTransform(
                    //         bsu.lightColorName
                    //     )?.targetOrientation;
                    // }
                    if (
                        !bsuSelectedArm.targetPos &&
                        !bsuSelectedArm.targetOr &&
                        builder.room.bsuPortLink[bsuSelectedArm.lightColorName].portName != ""
                    ) {
                        const trocarTransform = builder.room.getPortTransform(
                            bsuSelectedArm.lightColorName
                        );
                        if (trocarTransform) {
                            bsuSelectedArm.targetPos = trocarTransform.target;
                            bsuSelectedArm.targetOr = trocarTransform.targetOrientation;
                        }
                    }
                    bsuSelectedArm.moveElbow(intersect.point);
                    bsuSelectedArm.rotatePort();
                    break;
                case IRobotJoints.Wrist:
                    const effPos = bsuSelectedArm.getEffectorPos();
                    const targetOrientation = new Vector3()
                        .subVectors(effPos, intersect.point)
                        .normalize();
                    const colorName = bsuSelectedArm.lightColorName;
                    const portName = builder.room.bsuPortLink[colorName].portName;
                    if (portName) {
                        if (!builder.room.SM_patient) return;
                        const trocar = builder.room.SM_trocar[portName];
                        const oldQ = trocar.group.quaternion.clone();
                        const q = new Quaternion().setFromUnitVectors(
                            new Vector3(0.0, -1.0, 0.0),
                            targetOrientation
                        );
                        trocar.group.quaternion.copy(q);
                        trocar.group.updateMatrixWorld(true);
                        if (trocar.isDepthValid(builder.room.SM_patient)) {
                            moveBSU(colorName);
                            bsuSelectedArm.rotatePort();
                        } else {
                            trocar.group.quaternion.copy(oldQ);
                            trocar.group.updateMatrixWorld(true);
                        }
                    }
                    break;
            }
        }
        return;
    }
    if (!builder.room.selectedObject) return;
    moveSelectedObject(mouse);
}

function processPayload(payload: {
    obj: Robot | StaticMesh | undefined;
    addedToScene: boolean;
    id: string;
}): boolean | undefined {
    let targetMesh: Object3D[] | undefined = undefined;
    let sampler: MeshSurfaceSampler | undefined = undefined;
    if (payload.obj) {
        switch (payload.obj.type) {
            case AssetType.Trocar:
                if (builder.room.SM_patient) {
                    targetMesh = builder.room.SM_patient.meshes;
                }
                if (builder.room.SM_patient_sampler) {
                    sampler = builder.room.SM_patient_sampler;
                }
                break;
            case AssetType.Stack:
            case AssetType.Console:
            case AssetType.Robot:
            case AssetType.Trolley:
            case AssetType.Anesthesia:
            case AssetType.Surgeon:
            case AssetType.Slave:
                if (builder._floor) {
                    targetMesh = [builder._floor];
                }
                if (builder._floorSampler) {
                    sampler = builder._floorSampler;
                }
                break;
        }
        if (targetMesh && sampler) {
            const sampledPosition = new Vector3();
            const targetNormal = new Vector3();

            // look for a free spot
            if (payload.obj.type === AssetType.Trocar) {
                sampler.sample(sampledPosition, targetNormal);
                targetMesh[0].localToWorld(sampledPosition);
                //targetMesh[0].localToWorld(targetNormal);
            } else {
                const iterMax = 200;

                let spawnX = 2.2;
                let spawnZ = -1.5;
                let minRang = -1.5;
                let maxRang = 1.5;

                if (payload.obj.type === AssetType.Robot) {
                    spawnX = 1.5;
                    spawnZ = -0.8;
                    minRang = -0.8;
                    maxRang = 0.8;
                }

                if (payload.obj instanceof StaticMesh) {
                    switch (payload.obj.type) {
                        case AssetType.Console:
                            spawnX = 2.2;
                            spawnZ = -1.5;
                            break;

                        case AssetType.Stack:
                            spawnX = 2.2;
                            spawnZ = 0.0;
                            break;

                        case AssetType.Anesthesia:
                            spawnX = 2.2;
                            spawnZ = 1.5;
                            break;

                        case AssetType.Trolley:
                            spawnX = -2.2;
                            spawnZ = -1.5;
                            break;

                        case AssetType.Surgeon:
                            spawnX = -2.2;
                            spawnZ = 0.0;
                            break;

                        case AssetType.Slave:
                            spawnX = -2.2;
                            spawnZ = 1.5;
                            break;
                    }
                }

                sampler.sample(sampledPosition, targetNormal);
                targetMesh[0].localToWorld(sampledPosition);
                //check collision with the object in the scene
                sampledPosition.x = spawnX;
                sampledPosition.z = spawnZ;
                let iter = 0;
                if (payload.obj instanceof Robot) {
                    //check collision with the object in the scene
                    let isValid = payload.obj.setRootPosition(sampledPosition, builder.room);
                    //find a spot that is free
                    while (!isValid && iter < iterMax) {
                        sampledPosition.z += 0.25;
                        if (sampledPosition.z > maxRang) {
                            sampledPosition.x = -sampledPosition.x;
                            sampledPosition.z = spawnZ;
                        }
                        //check collision with the object in the scene
                        isValid = payload.obj.setRootPosition(sampledPosition, builder.room);
                        iter++;
                    }
                } else {
                    //check collision with the object in the scene
                    let isValid = payload.obj.setPosition(sampledPosition, builder.room);
                    //find a spot that is free
                    while (!isValid && iter < iterMax) {
                        sampledPosition.z += 0.25;

                        if (sampledPosition.z > maxRang) {
                            sampledPosition.x = -sampledPosition.x;
                            sampledPosition.z = minRang;
                        }
                        //check collision with the object in the scene
                        isValid = payload.obj.setPosition(sampledPosition, builder.room);
                        iter++;
                    }
                }
                targetMesh[0].localToWorld(targetNormal);
            }
            return dragNew({ point: sampledPosition, normal: targetNormal });
        }
        return false;
    }
}

function handlePointerDown(x: number, y: number): void {
    mouseDown = true;
    //GraphicService.control.enabled = false;
    const mouse = getPointer(x, y);
    pointerStart.copy(mouse);
    const castMeshes = GraphicService.armMeshes.concat(GraphicService.castableMeshes);
    const intersect = checkIntersect(mouse, castMeshes);
    if (intersect) {
        getSelectedObject(intersect);
        /* if (bsuSelectedArm) {
            const shoulder = bsuSelectedArm.getJointPos(2);
            const elbow = bsuSelectedArm.getJointPos(4);
            const wrist = bsuSelectedArm.getJointPos(6);
        } */
        GraphicService.control.update();
        GraphicService.control.enableRotate = false;
        //GraphicService.control.dispatchEvent({ type: "mousedown" });
    } else {
        //GraphicService.control.enabled = true;
        clearSelection();
    }
}
function handlePointerUp(x: number, y: number): void {
    mouseDown = false;
    GraphicService.control.enabled = true;
    GraphicService.control.enableRotate = true;
    GraphicService.sphereHelper.updateRadius(0);
    if (dragging) {
        dragging = false;
        firstMove = true;
        selectionEvents.emit("endDragging");
        return;
    } else {
        if (payload.obj) {
            endDrag();
            return;
        }

        const mouse = getPointer(x, y);
        const intersect = checkIntersect(
            mouse,
            GraphicService.armMeshes.concat(GraphicService.castableMeshes)
        );
        if (intersect) {
            builder.room.selectedObject = getSelectedObject(intersect);
            if (builder.room.selectedObject) {
                GraphicService.highlightObject(builder.room.selectedObject);
                switch (builder.room.selectedObject.type) {
                    case AssetType.PatientBed:
                        const patientBed = builder.room.selectedObject as {
                            type: AssetType;
                            SM_Patient: StaticMesh;
                            SM_Bed: StaticMesh;
                        };
                        selectionEvents.emit("objectSelected", patientBed.SM_Patient);
                        break;
                    case AssetType.Trocar:
                    case AssetType.Robot:
                    case AssetType.Console:
                    case AssetType.Stack:
                    case AssetType.Trolley:
                    case AssetType.Anesthesia:
                    case AssetType.Surgeon:
                    case AssetType.Slave:
                        selectionEvents.emit("objectSelected", builder.room.selectedObject);
                        break;
                }
            }
        }
    }
}
function checkIntersect(
    mouse: Vector2,
    castables: Object3D[] = GraphicService.castableMeshes
): undefined | Intersection {
    GraphicService.raycaster.setFromCamera(mouse, GraphicService.camera);
    if (castables.length > 0) {
        const intersects = GraphicService.raycaster.intersectObjects(castables, true);
        if (intersects.length > 0) {
            return intersects[0];
        }
    }
    return;
}

function getSelectedObject(
    intersect: Intersection
):
    | undefined
    | Robot
    | StaticMesh
    | { type: AssetType; SM_Patient: StaticMesh; SM_Bed: StaticMesh } {
    switch (intersect.object.userData.type) {
        case AssetType.Robot:
            if (intersect.object.userData.bsu) {
                const bsu = intersect.object.userData.bsu as Robot;
                if (intersect.object.userData.joint === IRobotJoints.Elbow) {
                    bsuSelectedArm = bsu;
                } else {
                    if (intersect.object.userData.joint === IRobotJoints.base)
                        builder.room.selectedObject = bsu;
                    else builder.room.selectedObject = undefined;
                }
                return bsu;
            }
            break;
        case AssetType.Patient:
        case AssetType.Bed:
            if (intersect.object.userData.staticmesh) {
                const patientBed = intersect.object.userData.patientBed as {
                    SM_Patient: StaticMesh;
                    SM_Bed: StaticMesh;
                };
                const result = {
                    type: AssetType.PatientBed,
                    SM_Patient: patientBed.SM_Patient,
                    SM_Bed: patientBed.SM_Bed,
                };
                builder.room.selectedObject = result;
                return result;
            }
            break;
        case AssetType.Console:
        case AssetType.Stack:
        case AssetType.Trolley:
        case AssetType.Anesthesia:
        case AssetType.Surgeon:
        case AssetType.Slave:
        case AssetType.Trocar:
            if (intersect.object.userData.staticmesh) {
                const sm = intersect.object.userData.staticmesh as StaticMesh;
                builder.room.selectedObject = sm;
                return sm;
            }
            break;
    }
    return;
}

function moveBSU(colorName: string): Robot | undefined {
    if (colorName && colorName !== "") {
        if (builder.room.bsuPortLink[colorName]) {
            const bsuName = builder.room.bsuPortLink[colorName].bsuName;
            const transform = builder.room.getPortTransform(colorName);
            if (bsuName !== "") {
                if (bsuName.startsWith(Inventory.iBsu.name)) {
                    const bsu = builder.room.SM_ibsu[bsuName];
                    if (transform) {
                        bsu.IKSolve(transform.target, transform.targetOrientation, {
                            start: -1,
                            end: bsu._dim - 5,
                        });
                        return bsu;
                        //console.log(bsu.getOrErro(transform.targetOrientation));
                        //if (bsu.getOrErro(transform.targetOrientation) > 0.01)
                        //bsu.rotatePort();
                    }
                } else if (bsuName.startsWith(Inventory.vBsu.name)) {
                    const transform = builder.room.getPortTransform(colorName);
                    const bsu = builder.room.SM_vbsu[bsuName];
                    if (transform && bsu) {
                        bsu.IKSolve(transform.target, transform.targetOrientation, {
                            start: -1,
                            end: bsu._dim - 5,
                        });
                        return bsu;
                        //console.log(bsu.getOrErro(transform.targetOrientation));
                        //if (bsu.getOrErro(transform.targetOrientation) > 0.01)
                        //bsu.rotatePort();
                    }
                }
            }
        }
    }
}
function moveSelectedObject(mouse: Vector2): void {
    if (!builder.room.selectedObject) return;

    switch (builder.room.selectedObject.type) {
        case AssetType.Trocar:
            if (builder.room.SM_patient) {
                const zone = builder.room.SM_patient.group.userData["zone"];
                const portTarget = zone
                    ? getIntersectPosition(mouse, [zone])
                    : getIntersectPosition(mouse, builder.room.SM_patient.meshes);
                if (portTarget) {
                    const normal = portTarget.face?.normal;
                    dragging = true;
                    if (firstMove) {
                        selectionEvents.emit("startDragging");
                        firstMove = false;
                    }
                    const sm = builder.room.selectedObject as StaticMesh;
                    if (sm) {
                        const oldPos = sm.group.position.clone();
                        sm.setPosition(portTarget.point, undefined, false);
                        const colorname = builder.room.trocarToColor[sm.name];
                        if (colorname) {
                            if (builder.room.SM_patient) {
                                if (!sm.isDepthValid(builder.room.SM_patient)) {
                                    sm.setPosition(oldPos, undefined, false);
                                } else {
                                    const bsu = moveBSU(builder.room.trocarToColor[sm.name]);
                                    if (bsu) {
                                        if (
                                            bsu.getEffectorPos().distanceTo(sm.getPosition()) > 0.01
                                        ) {
                                            sm.group.position.copy(bsu.getEffectorPos());
                                            sm.group.updateMatrixWorld(true);
                                        }
                                    }
                                }
                            }
                        } else if (normal) {
                            const transform = new Matrix3().setFromMatrix4(
                                portTarget.object.matrixWorld
                            );
                            normal.applyMatrix3(transform);
                            normal.multiplyScalar(-1);
                            normal.normalize();
                            const current = new Vector3(0.0, -1.0, 0.0);
                            const q = new Quaternion().setFromUnitVectors(current, normal);
                            sm.group.quaternion.copy(q);
                            sm.group.updateMatrixWorld(true);
                        }
                    }
                }
            }
            break;
        case AssetType.Robot:
            if (builder._floor) {
                const target = getIntersectPosition(mouse, [builder._floor]);
                if (target) {
                    const bsu = builder.room.selectedObject as Robot;
                    if (bsu) {
                        dragging = true;
                        if (firstMove) {
                            selectionEvents.emit("startDragging");
                            firstMove = false;
                            bsu.savePose();
                        }
                        if (bsu.setRootPosition(target.point, builder.room)) {
                            if (!bsu.targetPos && !bsu.targetOr) {
                                const trocarTransform = builder.room.getPortTransform(
                                    bsu.lightColorName
                                );
                                if (trocarTransform) {
                                    bsu.targetPos = trocarTransform.target;
                                    bsu.targetOr = trocarTransform.targetOrientation;
                                }
                            }
                            if (bsu.targetPos) {
                                bsu.IKSolve(bsu.targetPos, bsu.targetOr, {
                                    start: -1,
                                    end: bsu._dim - 5,
                                    savePos: false,
                                });
                            }
                        }
                    }
                }
            }
            break;
        case AssetType.PatientBed:
            break;
        case AssetType.Console:
        case AssetType.Stack:
        case AssetType.Trolley:
        case AssetType.Anesthesia:
        case AssetType.Surgeon:
        case AssetType.Slave:
            if (builder._floor) {
                const SM_target = getIntersectPosition(mouse, [builder._floor]);
                if (SM_target) {
                    dragging = true;
                    if (firstMove) {
                        selectionEvents.emit("startDragging");
                        firstMove = false;
                    }
                    const sm = builder.room.selectedObject as StaticMesh;
                    if (sm) {
                        sm.setPosition(SM_target.point, builder.room);
                    }
                }
            }
            break;
    }
    return;
}
function onStartDrag(id: string): {
    obj: Robot | StaticMesh | undefined;
    addedToScene: boolean;
    id: string;
} {
    payload.addedToScene = false;
    payload.id = id;
    switch (id) {
        case "Data 4":
        case "console":
            if (builder.room.numberOfObjects.console >= Inventory.MaxConsole) break;
            payload.obj = Inventory.SM_console;
            break;
        case "Data 5":
        case "stack":
            if (builder.room.numberOfObjects.stack >= Inventory.MaxStack) break;
            payload.obj = Inventory.SM_stack;
            break;
        case "trolley":
            if (builder.room.numberOfObjects.trolley >= Inventory.MaxTrolley) break;
            payload.obj = Inventory.SM_trolley;
            break;
        case "anesthesia":
            if (builder.room.numberOfObjects.anesthesia >= Inventory.MaxAnesthesia) break;
            payload.obj = Inventory.SM_anesthesia;
            break;
        case "surgeon":
            if (builder.room.numberOfObjects.surgeon >= Inventory.MaxSurgeon) break;
            payload.obj = Inventory.SM_surgeon;
            break;
        case "slave":
            if (builder.room.numberOfObjects.slave >= Inventory.MaxSlave) break;
            payload.obj = Inventory.SM_slave;
            break;
        case "Data 2":
        case "i_bsu":
            if (builder.room.numberOfObjects.bsu >= Inventory.MaxBSU) break;
            payload.obj = Inventory.iBsu.clone();
            break;
        case "v_bsu":
            if (builder.room.isVBSU) break;
            payload.obj = Inventory.vBsu;
            break;
        case "trocar":
            if (builder.room.numberOfObjects.trocar >= Inventory.MaxTrocar) break;
            payload.obj = Inventory.SM_trocar.clone();
            break;

        /* if (builder.room.numberOfObjects.trocar >= Inventory.MaxTrocar) break;
            payload.obj = Inventory.SM_trocar.clone();
            payload.obj.meshes[0].material = Inventory.M_trocar2;
            break; */
    }
    return payload;
}

export function dropPayload(payload: {
    obj: StaticMesh | Robot | undefined;
    addedToScene: boolean;
    id: string;
}): boolean {
    if (!processPayload(payload)) {
        endDrag();
        return false;
    }
    endDrag();
    return true;
}

function getPointer(x: number, y: number): Vector2 {
    const mouse = new Vector2();
    mouse.x = (x / GraphicService.divRef.clientWidth) * 2 - 1;
    mouse.y = -(y / GraphicService.divRef.clientHeight) * 2 + 1;
    return mouse;
}

function dragNew(intersect: { point: Vector3; normal: Vector3 | undefined }): boolean | undefined {
    if (!payload.obj) return false;
    const pos = intersect.point;
    const normal = intersect.normal?.clone();
    if (payload.obj instanceof Robot) {
        if (!payload.addedToScene) {
            switch (payload.id) {
                case "i_bsu":
                    if (builder.room.numberOfObjects.bsu > Inventory.MaxBSU) {
                        return false;
                    }
                    if (payload.obj.setRootPosition(pos, builder.room)) {
                        const color = Inventory.getAvailableColor();
                        if (color !== undefined) {
                            payload.obj.setLight(color);
                            builder.room.numberOfObjects.bsu++;
                            GraphicService.scene.add(payload.obj.group);
                            GraphicService.scene.add(payload.obj.collisiongroup);
                            builder.room.SM_ibsu[payload.obj.name] = payload.obj;
                            payload.obj.setPose("standard");
                            payload.addedToScene = true;
                            return true;
                            //moveBSU(color.name);
                        } else return false;
                    } else return false;

                case "v_bsu":
                    if (payload.obj.setRootPosition(pos, builder.room)) {
                        //if (!builder.room.SM_vbsu[payload.obj.name])
                        //    builder.room.numberOfObjects.bsu++;
                        GraphicService.scene.add(payload.obj.group);
                        GraphicService.scene.add(payload.obj.collisiongroup);
                        builder.room.SM_vbsu[payload.obj.name] = payload.obj;
                        builder.room.isVBSU = true;
                        payload.obj.setPose("standard");
                        payload.addedToScene = true;
                        return true;
                    } else return false;
                //moveBSU("white");
            }
            return false;
        } else {
            return payload.obj.setRootPosition(pos, builder.room);
            //moveBSU(payload.obj.lightColorName);
        }
    } else if (payload.obj instanceof StaticMesh) {
        if (!payload.addedToScene) {
            switch (payload.obj.type) {
                case AssetType.Trocar:
                    if (payload.obj.setPosition(pos, undefined, false)) {
                        if (normal) {
                            if (!builder.room.SM_patient) return;
                            //const dropzone = builder.room.SM_patient.group.userData["dropzone"];
                            normal.multiplyScalar(-1);
                            //builder.room.SM_patient?.group.userData["zone"]?.localToWorld(normal);
                            /* if (dropzone) {
                                dropzone.updateMatrix();
                                dropzone.localToWorld(normal);
                                console.log("dropZone");
                            } else {
                                builder.room.SM_patient.meshes[0].localToWorld(normal);
                            } */
                            const dropZone = builder.room.SM_patient.group.userData["dropzone"];
                            const transform = new Matrix3().setFromMatrix4(dropZone.matrixWorld);
                            normal.applyMatrix3(transform);
                            normal.normalize();
                            const current = new Vector3(0.0, -1.0, 0.0);
                            const q = new Quaternion().setFromUnitVectors(current, normal);
                            q.normalize();
                            payload.obj.group.quaternion.copy(q);
                            payload.obj.group.updateMatrixWorld(true);
                        }
                        builder.room.addPort(payload.obj, payload.obj.name);
                        return true;
                    } else return false;

                case AssetType.Console:
                    if (payload.obj.setPosition(pos, builder.room)) {
                        builder.room.addConsole();
                        payload.addedToScene = true;
                        return true;
                    } else return false;
                case AssetType.Stack:
                    if (payload.obj.setPosition(pos, builder.room)) {
                        builder.room.addStack();
                        payload.addedToScene = true;
                        return true;
                    } else return false;
                case AssetType.Trolley:
                    if (payload.obj.setPosition(pos, builder.room)) {
                        builder.room.addTrolley();
                        payload.addedToScene = true;
                        return true;
                    } else return false;
                case AssetType.Anesthesia:
                    if (payload.obj.setPosition(pos, builder.room)) {
                        builder.room.addAnesthesia();
                        payload.addedToScene = true;
                        return true;
                    } else return false;
                case AssetType.Surgeon:
                    if (payload.obj.setPosition(pos, builder.room)) {
                        builder.room.addSurgeon();
                        payload.addedToScene = true;
                        return true;
                    } else return false;
                case AssetType.Slave:
                    if (payload.obj.setPosition(pos, builder.room)) {
                        builder.room.addSlave();
                        payload.addedToScene = true;
                        return true;
                    } else return false;
            }
            return false;
        }
        switch (payload.obj.type) {
            case AssetType.Trocar:
                if (normal) {
                    /* normal.multiplyScalar(-1);
                    const q = new Quaternion().setFromUnitVectors(
                        new Vector3(0.0, 0.0, 1.0),
                        normal
                    );
                    q.normalize();
                    (payload.obj as StaticMesh).group.quaternion.copy(q);
                    (payload.obj as StaticMesh).group.updateMatrixWorld(true); */
                }
                return payload.obj.setPosition(pos, undefined, false);
            case AssetType.Console:
            case AssetType.Stack:
            case AssetType.Trolley:
            case AssetType.Anesthesia:
            case AssetType.Surgeon:
            case AssetType.Slave:
                return payload.obj.setPosition(pos, builder.room);
        }
    } else return false;
}
function endDrag(): void {
    payload.obj = undefined;
    payload.addedToScene = false;
    builder.room.updateCastables();
}
function clearSelection(): void {
    builder.room.selectedObject = undefined;
    bsuSelectedArm = undefined;
    selectedJoints = undefined;
    GraphicService.clearHighlight();
    selectionEvents.emit("clearSelection");
}

export { onMouseDown, onMouseUp, onMouseMove, onStartDrag, onArmHover, moveBSU };
